/**
 * NavSecondary
 *
 * @module components/Nav/NavSecondary
 */

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import HelpIcon from '../../../static/icons/header_help.svg';
import HelpDarkIcon from '../../../static/icons/header_help_dark.svg';
import LogoPandoraIcon from '../../../static/icons/logo_pandora.svg';
import LogoPlusIcon from '../../../static/icons/logo_pandora_plus.svg';
import Avatar from '../../components/Avatar';
import ButtonLink, { ButtonLinkStyles } from '../../components/ButtonLink';
import Icon from '../../components/Icon/Icon';
import { AriaAttributes, messages as AriaValues } from '../../constants/AccessibilityConstants';
import { messages as authMessages } from '../../constants/Anon';
import { AppConstants, NavThemes } from '../../constants/App';
import * as CommunityConstants from '../../constants/Community';
import DropdownConstants from '../../constants/Dropdown';
import QA from '../../constants/QATags';
import { messages as settingsNav } from '../../constants/SettingsNav';
import {
  getHelpUrl,
  getLoginPath,
  getPremiumUpgradePath,
  getRegisterPath,
  getUpgradePath,
} from '../../lib/RouteUtils';
import { createBEM } from '../../lib/utils/css';
import { HeaderDropdownKey } from '../../tools/Dropdowns/HeaderDropdown';

import './NavSecondary.scss';

export class NavSecondary extends React.Component {
  static propTypes = {
    openDropdown: PropTypes.func,
    theme: PropTypes.string.isRequired,
    listenerImgUrl: PropTypes.string,
    branding: PropTypes.string.isRequired,
    isAnonymous: PropTypes.bool.isRequired,
    isScrolled: PropTypes.bool.isRequired,
    isRightRailActive: PropTypes.bool,
    updateSearchOverlay: PropTypes.func,
    nowPlayingPath: PropTypes.string,
    productTierIsAdSupported: PropTypes.bool,
    productTierIsPlus: PropTypes.bool,
    showColdStart: PropTypes.bool,
    isSearchOpen: PropTypes.bool,
    signUpClicked: PropTypes.func,
    logInClicked: PropTypes.func,
  };

  onHelpClicked = () => {
    window.open(getHelpUrl());
  };

  onSettingsMenuClicked = event => {
    const rect = event.target.getBoundingClientRect();
    this.props.openDropdown({
      menuType: HeaderDropdownKey,
      config: {
        viewMode: CommunityConstants.ViewMode.DROPDOWN_PROFILE,
        position: rect,
        // We need to make a few positioning adjustments to put the dropdown
        // avatar in the right place
        positionAdjustments: {
          position: 'fixed',
          horizontal: this.props.listenerImgUrl ? 5 : 5,
          // FIXME: Not sure why, but positioning is one pixel different between an Avatar with
          // an image and one without. Can't spend any more time right now figuring out why,
          // so in the meantime this hack will have to do.
          vertical: -4,
        },
        alignHorizontal: DropdownConstants.ALIGN_RIGHT,
        alignVertical: DropdownConstants.ALIGN_TOP,
        listenerImgUrl: this.props.listenerImgUrl,
        updateSearchOverlay: this.props.updateSearchOverlay,
        focusTarget: event.target,
      },
    });
  };

  renderUpgrade = () => {
    let upgradeButtonText;
    let upgradeButtonPath;
    if (this.props.productTierIsAdSupported) {
      upgradeButtonText = settingsNav.upgrade;
      upgradeButtonPath = getUpgradePath();
    } else if (this.props.productTierIsPlus) {
      upgradeButtonText = settingsNav.getPremium;
      upgradeButtonPath = getPremiumUpgradePath();
    } else {
      return;
    }

    const buttonTheme =
      this.props.theme === NavThemes.DARK && !this.props.isSearchOpen
        ? ButtonLinkStyles.NAV_UPGRADE_DARKTHEME
        : ButtonLinkStyles.NAV_UPGRADE_LIGHTTHEME;
    const upgradeStyleNames = [ButtonLinkStyles.NAV, buttonTheme];

    return (
      <li className='NavSecondary__item'>
        <ButtonLink
          to={upgradeButtonPath}
          title={upgradeButtonText}
          styleNames={upgradeStyleNames}
          handleClick={this.updateSearchOverlay}
        />
      </li>
    );
  };

  updateSearchOverlay = () => {
    if (this.props.updateSearchOverlay) {
      this.props.updateSearchOverlay();
    }
  };

  renderLogo = () => {
    const iconQATag = this.props.branding === AppConstants.BRANDING_PANDORA_PLUS ? QA.HPOI : QA.HPI;
    const icon =
      this.props.branding === AppConstants.BRANDING_PANDORA_PLUS ? LogoPlusIcon : LogoPandoraIcon;
    const logoClasses = createBEM('NavSecondary__logo', {
      plusIcon: this.props.branding === AppConstants.BRANDING_PANDORA_PLUS,
      lightIcon: this.props.theme === NavThemes.LIGHT && !this.props.showColdStart,
      darkIcon:
        (!this.props.showColdStart && this.props.theme === NavThemes.LIGHT) ||
        (!this.props.showColdStart &&
          !this.props.isScrolled &&
          this.props.theme !== NavThemes.DARK),
    });
    const iconAriaAttributes = {
      [AriaAttributes.HIDDEN]: true,
    };

    const linkAriaAttributes = {
      [AriaAttributes.LABEL]: AriaValues.pandoraLogo.defaultMessage,
    };

    return (
      <li className='NavSecondary__item'>
        <Link
          to={this.props.nowPlayingPath}
          className={logoClasses}
          {...linkAriaAttributes}
          onClick={this.updateSearchOverlay}
        >
          <Icon
            className='NavSecondary__logo--icon'
            qaTag={iconQATag}
            glyph={icon}
            aria={iconAriaAttributes}
          />
        </Link>
      </li>
    );
  };

  renderSecondaryNavAnon = () => {
    const signUpStyleNames = [
      ButtonLinkStyles.NAV,
      this.props.theme === NavThemes.DARK
        ? ButtonLinkStyles.NAV_SIGNUP_DARKTHEME
        : ButtonLinkStyles.NAV_SIGNUP_LIGHTTHEME,
    ];
    const loginStyleNames = [
      ButtonLinkStyles.NAV,
      ButtonLinkStyles.NAV_LOGIN_ANON,
      this.props.theme === NavThemes.DARK
        ? ButtonLinkStyles.NAV_LOGIN_DARKTHEME
        : ButtonLinkStyles.NAV_LOGIN_LIGHTTHEME,
    ];
    return (
      <ul className='NavSecondary'>
        {this.renderLogo()}
        <li className='NavSecondary__item NavSecondary__last'>
          <ButtonLink
            to={getRegisterPath()}
            handleClick={this.props.signUpClicked}
            title={authMessages.signUp}
            styleNames={signUpStyleNames}
            qaTag={QA.HSB}
          />
          <ButtonLink
            to={getLoginPath()}
            handleClick={this.props.logInClicked}
            title={authMessages.login}
            styleNames={loginStyleNames}
            qaTag={QA.HLB}
          />
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <span className='NavSecondary__help' onClick={this.onHelpClicked}>
            <Icon
              className='icon-help'
              glyph={this.props.theme === NavThemes.DARK ? HelpIcon : HelpDarkIcon}
              qaTag={QA.HHI}
            />
          </span>
        </li>
      </ul>
    );
  };

  renderSecondaryNav = () => {
    const { theme, isRightRailActive, listenerImgUrl, showColdStart } = this.props;
    const classes = createBEM('NavSecondary', {
      [`${theme}Theme`]: !isEmpty(theme),
      rightRail: isRightRailActive,
      showColdStart,
    });
    return (
      <ul className={classes}>
        {this.renderLogo()}
        {this.renderUpgrade()}
        <li className='NavSecondary__item NavSecondary__last'>
          <Avatar
            onClick={this.onSettingsMenuClicked}
            listenerImgUrl={listenerImgUrl}
            qaTag={QA.HPRI}
          />
        </li>
      </ul>
    );
  };

  render() {
    return this.props.isAnonymous ? this.renderSecondaryNavAnon() : this.renderSecondaryNav();
  }
}

export default NavSecondary;
